import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, OnDestroy, signal, computed, inject, input, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { UserRoleEnum, ConferenceMetadataKeyEnum } from "../../core-ui.enums";
import {
  AgentParam,
  IInteraction,
  IRecorderInfo,
  IRecorderOptions,
  RecorderStateEnum,
  PublicParam,
  StorageStatus,
  IStorageStatusResponse,
} from "../../models";
import { AppConfigService, FEATURES } from "../../services/app.config.service";
import { GenericErrorHandler } from "../../services/error-handlers.service";
import {
  RecorderService,
  isRecorderInUse,
  isSameRecorder,
} from "../../services/recorder.service";
import { UserService } from "../../services/user.service";
import { NotificationService } from "../../services/notification.service";
import { debug, debugError } from "../../services/utils";
import { RoomComponent } from "../room/room.component";
import { filter } from "rxjs/operators";
import { RecorderMetadata } from "../../models/Metadata";
import {
  ApplicationService,
  ConferenceService,
  RecorderEndedAction,
  RecorderStartedAction,
  WindowEventService,
} from "../../services";
import { CobrowseService } from "../../services/cobrowse.service";

@Component({
  selector: "app-recorder",
  templateUrl: "./recorder.component.html",
  styleUrls: ["./recorder.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RecorderComponent implements OnInit, OnDestroy {
  private config = inject(AppConfigService);
  private recorderService = inject(RecorderService);
  private errorHandler = inject(GenericErrorHandler);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private userService = inject(UserService);
  private conferenceService = inject(ConferenceService);
  private notification = inject(NotificationService);
  private cobrowser = inject(CobrowseService);
  private windowEventService = inject(WindowEventService);
  private application = inject(ApplicationService);
  private room = inject(RoomComponent, { optional: true });
  private destroyRef = inject(DestroyRef);

  readonly options = input<IRecorderOptions>(undefined);
  readonly interaction = input<IInteraction>(undefined);

  readonly started = output<IRecorderInfo>();
  readonly stopped = output<IRecorderInfo>();

  recorder: IRecorderInfo;
  recorderState: RecorderStateEnum;
  recorderAbortedTimeout;
  recorderAbortedRetryWait: number;
  recorderRetry = false;

  isAgent: boolean;
  isCustomer: boolean;
  isGuestAgent: boolean;
  isRecordingRemotely = signal(false);
  isRecorderEnabled: boolean;
  isWhisper: boolean;

  recorderSubscription: Subscription;

  // the conference this recorder belongs to
  private conferenceId: string;
  private isActivating = false;
  private recovery: Promise<void>;
  private isRecoveryRequested = false;
  private destroyed = false;

  storageStatus = signal<StorageStatus>(undefined);
  storageStatusMessage = signal<string>(undefined);
  isStorageBlocked = computed(() => this.storageStatus() === "BLOCKED");
  isStorageWarn = computed(() => this.storageStatus() === "WARNING");
  isStatusAvailable = computed(
    () => this.isStorageBlocked() || this.isStorageWarn()
  );

  constructor() {
    const config = this.config;

    this.recorderAbortedRetryWait = config.recorderAbortWaitTime;
    this.isAgent = this.userService.getActiveUser().hasRole(UserRoleEnum.agent);
    this.isGuestAgent = this.userService.isGuestAgent;
    this.isCustomer = this.userService
      .getActiveUser()
      .hasRole(UserRoleEnum.customer);
    this.isRecorderEnabled = this.config.publicParam(
      PublicParam.RECORDER_ENABLED
    ) as boolean;
  }

  ngOnInit(): void {
    if (!this.isRecorderEnabled) {
      return;
    }
    this.isWhisper = !!this.interaction().getWhisperMode();
    this.conferenceId = this.options()?.conferenceId;
    this.recorderService.enterConference(this.conferenceId);
    this.getActiveRecorder();
    this.conferenceService.conferenceMetadataSet$
        .pipe(filter((data) => data instanceof RecorderMetadata), takeUntilDestroyed(this.destroyRef))
        .subscribe((data: RecorderMetadata) => {
          this.isRecordingRemotely.set(true);
          this.cdr.detectChanges();
          this.notification.info("Recording", {
            body: "Session is being recorded",
          });
          if (this.room?.isChildWindow) {
            this.windowEventService.sendMessage(new RecorderStartedAction());
          }
        });
    this.conferenceService.conferenceMetadataRemoved$
        .pipe(filter((data) => data instanceof RecorderMetadata), takeUntilDestroyed(this.destroyRef))
        .subscribe((data: RecorderMetadata) => {
          this.isRecordingRemotely.set(false);
          // covers the case where we have more than one agents (not guests)
          // 1st has started recording, 2nd joins, 1st stops recording.
          if (this.isAgent && this.isRecorderActive) {
            this.recorder = null;
            this.recorderState = RecorderStateEnum.stopped;
          }
          this.cdr.detectChanges();
          this.notification.info("Recording", {
            body: "Session is no longer recorded",
          });
          if (this.room?.isChildWindow) {
            this.windowEventService.sendMessage(new RecorderEndedAction());
          }
        });
    this.subscribeToRecorder();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.recorderSubscription?.unsubscribe();
    this.recorderService.leaveConference(this.conferenceId);
    this.recorderService.reset();
    if (this.recorderAbortedTimeout) {
      clearTimeout(this.recorderAbortedTimeout);
      this.recorderAbortedTimeout = null;
    }
  }

  toggleRecorder() {
    if (this.isRecorderActive) {
      this.stopRecorder();
    } else {
      this.startRecorder();
    }
  }

  async startRecorder() {
    const options = this.options();
    if (
      !options ||
      (!options.audio && !options.video) ||
      this.isStorageBlocked()
    ) {
      return;
    }
    // drop the previous selection so its events do not affect the new recorder
    this.setRecorderLoading(false);
    if (options.audio === undefined || options.audio === null) {
      options.audio = options.audio;
    }
    if (options.video === undefined || options.video === null) {
      options.video = !this.config.agentParamEnabled(
        AgentParam.RECORDER_AUDIO_ONLY
      );
    }

    this.isActivating = true;
    try {
      const recorder = await this.recorderService.activate(options);
      if (!this.isCurrentConference) {
        // the agent left this conference, the response is stale
        return;
      }
      if (!recorder) {
        this.recorder = null;
        return;
      }
      this.selectRecorder(recorder);
      this.recorderService.storeRecorderInfo(
        options.conversationId,
        this.recorder
      );
    } catch (ex) {
      if (!this.isCurrentConference) {
        return;
      }
      this.errorHandler.handleError(ex);
      this.notification.error(
        this.translate.instant(
          "Could not start recording. Please try again or contact your administrator."
        )
      );
      this.recorder = null;
    } finally {
      this.isActivating = false;
      if (!this.destroyed) {
        this.cdr.detectChanges();
      }
      // this.activityService.loading(false);
    }
  }

  async stopRecorder() {
    if (this.isRecorderLocked) {
      return;
    }
    this.recorder.state = RecorderStateEnum.loading;
    try {
      await this.recorderService.stop(
        this.recorder.recorderId,
        this.recorder.conversationId,
        this.recorder.instanceId
      );
      await this.cobrowser.disableRecording();
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
    this.recorderService.clearRecorderInfo(this.options().conversationId);
    this.cdr.detectChanges();
  }

  setRecorderLoading(keepSelection = true) {
    this.recorder = {
      state: RecorderStateEnum.loading,
      recorderId: keepSelection ? this.recorder?.recorderId : undefined,
      instanceId: keepSelection ? this.recorder?.instanceId : undefined,
      conversationId: keepSelection ? this.recorder?.conversationId : undefined,
    };
    this.cdr.detectChanges();
  }

  async getRecorderStatus(): Promise<IStorageStatusResponse> {
    try {
      const res = await this.recorderService.getStorageProviderStatus(
        this.application.getActiveApplication().getId()
      );
      this.storageStatus.set(res.status);
      if (res.statusMessage) {
        this.storageStatusMessage.set(
          this.translate.instant(res.statusMessage)
        );
      }
      return res;
    } catch (ex) {
      debugError(ex);
    }
  }

  async getActiveRecorder() {
    if (!this.isAgent || !this.isRecorderAvailable) {
      return;
    }
    const { conversationId, conferenceId } = this.options();

    if (
      !this.isGuestAgent &&
      this.isRecorderConfigured &&
      this.config.agentParamEnabled(AgentParam.RECORDER_AUTO_START)
    ) {
      // starts a new recorder or returns the existing one of this conference
      await this.getRecorderStatus();
      if (!this.isStorageBlocked()) {
        this.startRecorder();
      }
      return;
    }

    // show any existing recording of this conference, without starting one
    const [recorder] = await Promise.all([
      this.recorderService.getCurrent(conversationId, conferenceId),
      this.getRecorderStatus(),
    ]);
    if (this.isStorageBlocked() || !this.isCurrentConference) {
      return;
    }
    // the agent may have pressed start in the meantime
    if (isRecorderInUse(recorder) && !this.isActivating && !this.recorder) {
      await this.selectRecorder(recorder);
      this.cdr.detectChanges();
    }
  }

  /**
   * Makes the recorder the one this component follows. Only its events are applied.
   * An 'ACTIVE' recorder will not send another startup event, and an 'INITIALISED' one
   * may have sent it before we selected it, so check its state here.
   */
  private async selectRecorder(recorder: IRecorderInfo) {
    if (recorder.state === RecorderStateEnum.active) {
      this.onRecorderActive(recorder);
      return;
    }

    this.recorder = recorder;
    if (recorder.state !== RecorderStateEnum.initialised) {
      return;
    }

    try {
      const info = await this.recorderService.getInfo(
        recorder.recorderId,
        recorder.conversationId,
        recorder.instanceId
      );
      const latest = { ...recorder, ...info };
      if (
        this.isSelected(latest) &&
        latest.state !== RecorderStateEnum.initialised
      ) {
        this.applyRecorderEvent(latest);
      }
    } catch (ex) {
      // the startup event will update the state
      debug(ex);
    }
  }

  /**
   * The backend may replace the recorder of this conference, in which case we get events
   * from a recorder we do not follow. Ask which one is current and follow that instead.
   */
  private recoverRecorder(event: IRecorderInfo) {
    if (
      !this.isAgent ||
      this.isActivating ||
      !isRecorderInUse(event) ||
      (!!event.conversationId &&
        event.conversationId !== this.options()?.conversationId)
    ) {
      return;
    }
    if (this.recovery) {
      // the replacement may not have been current when the running check asked
      this.isRecoveryRequested = true;
      return;
    }

    this.recovery = (async () => {
      try {
        const current = await this.recorderService.getCurrent(
          this.options().conversationId,
          this.conferenceId
        );
        if (
          !this.isCurrentConference ||
          this.isActivating ||
          !isRecorderInUse(current) ||
          this.isSelected(current)
        ) {
          return;
        }
        debug("recorder replaced", this.recorder, current);
        if (!!this.recorderAbortedTimeout) {
          clearTimeout(this.recorderAbortedTimeout);
          this.recorderAbortedTimeout = null;
        }
        await this.selectRecorder(current);
        this.cdr.detectChanges();
      } finally {
        this.recovery = null;
        if (this.isRecoveryRequested && !this.destroyed) {
          this.isRecoveryRequested = false;
          this.recoverRecorder(event);
        }
      }
    })();
  }

  private isSelected(recorder: IRecorderInfo): boolean {
    return isSameRecorder(this.recorder, recorder);
  }

  private get isCurrentConference(): boolean {
    return (
      !this.destroyed &&
      this.recorderService.isCurrentConference(this.conferenceId)
    );
  }

  private onRecorderActive(data: IRecorderInfo) {
    // both the startup event and the state check may report it
    const wasActive = this.isRecorderActive && this.isSelected(data);

    this.recorder = data;
    this.recorderService.storeRecorderInfo(
      this.options().conversationId,
      this.recorder
    );
    if (!!this.recorderAbortedTimeout) {
      clearTimeout(this.recorderAbortedTimeout);
      this.recorderAbortedTimeout = null;
    }
    this.cdr.detectChanges();

    if (wasActive) {
      return;
    }

    this.started.emit({ ...this.recorder });

    // same user id, joined as monitoring supervisor
    if (this.isWhisper) {
      this.isRecordingRemotely.set(true);
      return;
    }

    // notify others
    this.conferenceService.setConferenceMetadata(
      new RecorderMetadata(this.conferenceService.myself, true)
    );
  }

  subscribeToRecorder() {
    this.recorderSubscription = this.recorderService.eventReceived$.subscribe(
      (data) => {
        debug("recorder state changed", data);
        if (this.isSelected(data)) {
          this.applyRecorderEvent(data);
        } else {
          // events of other recorders (e.g. of a previous conference) are not ours
          this.recoverRecorder(data);
        }
      }
    );
  }

  private applyRecorderEvent(data: IRecorderInfo) {
    switch (data.state) {
      case RecorderStateEnum.active:
        // we may receive 'active' from an aborted stream
        this.onRecorderActive(data);
        break;
      case RecorderStateEnum.stopped:
        this.stopped.emit({
          ...this.recorder,
          state: RecorderStateEnum.stopped,
        });

        this.recorder = null;
        if (!!this.recorderAbortedTimeout) {
          clearTimeout(this.recorderAbortedTimeout);
          this.recorderAbortedTimeout = null;
        }
        this.cdr.detectChanges();

        if (this.isWhisper) {
          this.isRecordingRemotely.set(false);
          return;
        }

        this.conferenceService.removeConferenceMetadata(
          ConferenceMetadataKeyEnum.recorder
        );
        break;
      case RecorderStateEnum.failed:
        this.errorHandler.handleError(data.error);
        if (!!this.recorderAbortedTimeout) {
          clearTimeout(this.recorderAbortedTimeout);
          this.recorderAbortedTimeout = null;
        }
        this.recorderNotifyFailure();

        if (this.isWhisper) {
          this.isRecordingRemotely.set(false);
          return;
        }

        this.conferenceService.removeConferenceMetadata(
          ConferenceMetadataKeyEnum.recorder
        );
        break;
      case RecorderStateEnum.aborted:
        this.errorHandler.handleError(data.error);
        this.setRecorderLoading();
        // temporarily stopped because of a server/cluster error. will reconnect.
        // wait for some seconds to receive 'active' or 'failed' again.
        // 'active' and 'failed' will cancel this timeout
        this.recorderAbortedTimeout = setTimeout(() => {
          this.recorderNotifyFailure();
          // this.handleRecorderFailRetry(recorderSubscription);
        }, this.recorderAbortedRetryWait);

        if (this.isWhisper) {
          this.isRecordingRemotely.set(false);
          return;
        }

        this.conferenceService.removeConferenceMetadata(
          ConferenceMetadataKeyEnum.recorder
        );
        break;
    }
  }

  private recorderNotifyFailure() {
    this.notification.error(
      this.translate.instant(
        "Recording has stopped. Please contact your administrator."
      )
    );
    this.recorder = null;
    this.cdr.detectChanges();
  }

  /**
   * Deprecated for now. Kept recording abort-retry simple and let server do all the handling
   */
  async handleRecorderFailRetry(recorderSubscription: Subscription) {
    // clear previous recorder and subscription
    this.recorder = null;
    this.cdr.detectChanges();
    // retry once
    if (!this.recorderRetry) {
      debug("retrying recorder to start");
      this.recorderRetry = true;
      const existingRecorder = await this.recorderService.getCurrent(
        this.options().conversationId,
        this.conferenceId
      );
      // check if we have an existing active recorder before retrying.
      if (!!existingRecorder) {
        // get recorder info from server
        const zombieRecorder = await this.recorderService.getInfo(
          existingRecorder.recorderId,
          existingRecorder.conversationId,
          existingRecorder.instanceId
        );

        // clear previous recorder info
        this.recorderService.clearRecorderInfo(this.options().conversationId);

        // if the existing recorder is for some reason active, then stop before re-start.
        if (
          !!zombieRecorder &&
          zombieRecorder.state === RecorderStateEnum.active
        ) {
          await this.recorderService.stop(
            zombieRecorder.recorderId,
            zombieRecorder.conversationId,
            zombieRecorder.instanceId
          );
          this.startRecorder();
        } else {
          this.startRecorder();
        }
      } else {
        this.startRecorder();
      }
    } else {
      this.notification.error(
        this.translate.instant(
          "Recording has stopped and cannot be started again. Please contact your administrator."
        )
      );
    }
  }

  /* view accessors */

  get isActionable() {
    return (
      this.isRecorderEnabled &&
      this.isRecorderConfigured &&
      this.isAgent &&
      !this.isGuestAgent
    );
  }

  get isReadOnly() {
    return this.isRecorderEnabled && (this.isCustomer || this.isGuestAgent);
  }

  get isRecorderAvailable(): boolean {
    return this.config.featureEnabled(FEATURES.RECORDER);
  }

  get isRecorderActive(): boolean {
    return this.recorder && this.recorder.state === RecorderStateEnum.active;
  }

  get isRecorderStarting(): boolean {
    return (
      this.recorder &&
      (this.recorder.state === RecorderStateEnum.loading ||
        this.recorder.state === RecorderStateEnum.initialised)
    );
  }

  get isRecorderConfigured(): boolean {
    return this.config.agentParamEnabled(AgentParam.RECORDER_CONFIGURED);
  }

  get isRecorderButtonDisabled() {
    return (
      (this.isRecorderStarting || !this.isRecorderConfigured) &&
      !this.isRecorderActive
    );
  }

  get isRecorderLocked() {
    return (
      this.config.agentParam(AgentParam.RECORDER_AUTO_START) &&
      this.config.agentParam(AgentParam.RECORDER_LOCK)
    );
  }
}
